import { getCurrencySymbol } from '@angular/common';
import { Injectable } from '@angular/core';
import { BestFareResult, BffService } from '@core/bff';
import { AppConfigService } from '@core/index';
import { DateAggregator } from '@dynamic-components/booker-v2/shared/date-range-picker/types/date-aggregator.type';
import { TotalCost } from '@dynamic-components/booker-v2/shared/date-range-picker/types/total-cost.type';
import { TravelerGroup } from '@dynamic-components/booker-v2/shared/traveler-selector/types';
import { ComponentStore } from '@ngrx/component-store';
import { BOOKER_CONFIG, BookerResponse } from '@shared/booker';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { differenceInDays, format, parseISO } from 'date-fns';
import { combineLatest, Observable, of } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { createJBMonthObjForCL, enableAllDates } from '../utils/schedule.utils';
import {
  bookerAirComponentInitialState,
  BookerAirState,
  CustomBookerAirFareForm,
} from './booker-air.store.type';

@Injectable()
export class BookerAirStore extends ComponentStore<BookerAirState> {
  /** * UPDATERS ***/

  readonly updateBookerAirTripType = this.updater<string>(
    (state, intineraryType): BookerAirState => {
      return {
        ...state,
        bookerAirFaresForm: {
          ...state.bookerAirFaresForm,
          intineraryType,
        },
      };
    },
  );

  readonly updateBookerAirFaresForm = this.updater<CustomBookerAirFareForm>(
    (
      state,
      { originCode, destinationCode, intineraryType, fare, ADT, CHD, INF },
    ): BookerAirState => ({
      ...state,
      bookerAirFaresForm: {
        ...state.bookerAirFaresForm,
        originCode: originCode
          ? originCode
          : state.bookerAirFaresForm.originCode,
        destinationCode: destinationCode
          ? destinationCode
          : state.bookerAirFaresForm.destinationCode,
        intineraryType,
        fare,
        ADT,
        CHD,
        INF,
      },
    }),
  );

  readonly updateOriginDestinationCode = this.updater<{
    originCode: string[];
    destinationCode: string[];
  }>(
    (state, { originCode, destinationCode }): BookerAirState => ({
      ...state,
      bookerAirFaresForm: {
        ...state.bookerAirFaresForm,
        originCode,
        destinationCode,
      },
    }),
  );

  readonly updateDepartDate = this.updater<Date>(
    (state, departDate): BookerAirState => ({
      ...state,
      bookerAirFaresForm: {
        ...state.bookerAirFaresForm,
        departDate: [departDate],
      },
    }),
  );

  readonly updateScheduleEnabled = this.updater<boolean>(
    (state, scheduleEnabled): BookerAirState => ({
      ...state,
      scheduleEnabled,
    }),
  );

  readonly updatePricingEnabled = this.updater<boolean>(
    (state, pricingEnabled): BookerAirState => ({
      ...state,
      pricingEnabled,
    }),
  );

  readonly updateReturnDate = this.updater<Date>(
    (state, returnDate): BookerAirState => ({
      ...state,
      bookerAirFaresForm: {
        ...state.bookerAirFaresForm,
        returnDate: [returnDate],
      },
    }),
  );

  readonly updateApiFailed = this.updater<boolean>(
    (state, apiFailed): BookerAirState => ({ ...state, apiFailed }),
  );

  readonly updateContent = this.updater<BookerResponse>(
    (state, content): BookerAirState => ({
      ...state,
      content,
    }),
  );

  readonly updateDateAggregator = this.updater<DateAggregator>(
    (state, dateAggregator): BookerAirState => {
      // CMS might set the data in a wrong way.. Sending an array of two objects instead of just one.
      // In this case, update the states from dateAggregator using the first item from this array.
      // Bug found at DOT-10678
      const dateAggregatorStates = Array.isArray(dateAggregator.states)
        ? dateAggregator.states[0]
        : dateAggregator.states;
      return {
        ...state,
        content: {
          ...state.content,
          dateAggregator: {
            ...dateAggregator,
            states: {
              ...dateAggregatorStates,
            },
          },
        },
      };
    },
  );

  readonly updateFocusedInput = this.updater<'depart' | 'return'>(
    (state, focusedInput): BookerAirState => ({
      ...state,
      focusedInput,
    }),
  );

  /** ********************** SELECTORS ************************/

  readonly pricingEnabled$: Observable<boolean> = this.select(
    state => state.pricingEnabled,
  );

  readonly scheduleEnabled$: Observable<boolean> = this.select(
    state => state.scheduleEnabled,
  );

  readonly bffIntegrationEnabled$: Observable<boolean> = this.select(
    this.pricingEnabled$,
    this.scheduleEnabled$,
    (pricingEnabled, scheduleEnabled) => pricingEnabled || scheduleEnabled,
  );

  /**
   * Origin code chosen in the booker.
   * Emits values twice in a row when updated by form. Consumer should make use of debounceTime to counteract if required.
   */
  readonly originCode$: Observable<string> = this.select(
    state => state.bookerAirFaresForm.originCode[0],
  );

  /**
   * Destination code chosen in the booker.
   * Emits values twice in a row when updated by form. Consumer should make use of debounceTime to counteract if required.
   */
  readonly destinationCode$: Observable<string> = this.select(
    state => state.bookerAirFaresForm.destinationCode[0],
  );

  readonly departDate$: Observable<Date> = this.select(state =>
    state.bookerAirFaresForm.departDate &&
    state.bookerAirFaresForm.departDate[0]
      ? new Date(
          typeof state.bookerAirFaresForm.departDate[0] === 'string'
            ? parseISO(state.bookerAirFaresForm.departDate[0])
            : state.bookerAirFaresForm.departDate[0],
        )
      : null,
  );

  readonly returnDate$: Observable<Date> = this.select(state =>
    state.bookerAirFaresForm.returnDate &&
    state.bookerAirFaresForm.returnDate[0]
      ? new Date(
          typeof state.bookerAirFaresForm.returnDate[0] === 'string'
            ? parseISO(state.bookerAirFaresForm.returnDate[0])
            : state.bookerAirFaresForm.returnDate[0],
        )
      : null,
  );

  readonly getAllMonthsCache$ = this.select(state => state.monthPricingCache);

  readonly intineraryType$: Observable<string> = this.select(
    state => state.bookerAirFaresForm.intineraryType,
  );

  public findDateInCache = (
    date,
    cache: BookerAirState['monthPricingCache'],
    isInbound = false,
  ) => {
    const dateKey = date.getFullYear() + '-' + (date.getMonth() + 1);
    const pricesList = isInbound
      ? cache[dateKey]?.inbound
      : cache[dateKey]?.outbound;
    return pricesList?.find(obj => obj.date.getTime() === date.getTime());
  };

  public findDateInCacheArray = (
    date: Date,
    cache: {
      year: string;
      month: string;
      value: BestFareResult;
    }[],
    isInbound = false,
  ) => {
    const pricesList = cache.find(
      item =>
        item.month === String(date.getMonth() + 1) &&
        item.year === String(date.getFullYear()),
    );
    const bestFareArr = isInbound
      ? pricesList?.value?.inbound
      : pricesList?.value?.outbound;

    return bestFareArr?.find(obj => obj.date.getTime() === date.getTime());
  };

  readonly totalCost$: Observable<TotalCost> = this.select(
    this.departDate$,
    this.returnDate$,
    this.getAllMonthsCache$,
    this.intineraryType$,
    this.pricingEnabled$,
    (departDate, returnDate, pricingCache, itineraryType, pricingEnabled) => {
      if (
        pricingEnabled &&
        ((departDate && returnDate) ||
          (departDate && itineraryType === BOOKER_CONFIG.SEARCH_TYPES.ONE_WAY))
      ) {
        const currenyCurrencyCode =
          pricingCache[
            departDate.getFullYear() + '-' + (departDate.getMonth() + 1)
          ]?.currencyCode;

        const departPriceInCache = this.findDateInCache(
          departDate,
          pricingCache,
        );
        let returnPriceInCache;
        if (
          returnDate &&
          itineraryType === BOOKER_CONFIG.SEARCH_TYPES.ROUND_TRIP
        ) {
          returnPriceInCache = this.findDateInCache(
            returnDate,
            pricingCache,
            true,
          );
        }

        return {
          amount:
            departPriceInCache?.amount + (returnPriceInCache?.amount || 0) ||
            null,
          tax: departPriceInCache?.tax + (returnPriceInCache?.tax || 0) || null,
          currencyCode: currenyCurrencyCode || null,
        };
      } else {
        return null;
      }
    },
  );

  readonly fare$: Observable<string> = this.select(
    state => state.bookerAirFaresForm.fare,
  );

  readonly travelers$: Observable<TravelerGroup> = this.select(state => ({
    adults: state.bookerAirFaresForm.ADT,
    children: state.bookerAirFaresForm.CHD,
    infants: state.bookerAirFaresForm.INF,
  }));

  readonly apiFailed$ = this.select(state => state.apiFailed);

  readonly calendarStates$ = this.select(
    state => state.content?.dateAggregator?.states,
  );

  readonly content$ = this.select(state => state.content);

  /** Get round trip trip length in days */
  readonly tripLength$ = this.select(
    this.departDate$,
    this.returnDate$,
    this.content$,
    (departDate, returnDate, content): number => {
      const roundTripDefaultTripLength =
        content?.dateAggregator?.roundtripPricing?.roundTripDefaultTripLength;
      let tripLength = roundTripDefaultTripLength
        ? Number(roundTripDefaultTripLength)
        : this.appConfig.roundTrip.defaultTripLength;
      if (departDate && returnDate) {
        tripLength = Math.abs(differenceInDays(departDate, returnDate));
      }
      return tripLength;
    },
  );

  readonly focusedInput$ = this.select(state => state.focusedInput);

  /*
  Return Content and showCalendarRoundtripPrices.
  Check if showCalendarRoundtripPrices was sent in the CMS data(content).
  If this value is not in content, then a fallback from appconfig panel will be returned.
  REF: DOT-9920
  */
  getShowCalendarRoundtripPricesValue(content: BookerResponse): {
    content: BookerResponse;
    showCalendarRoundtripPrices: boolean;
  } {
    let showCalendarRoundtripPrices;
    if (
      content?.dateAggregator?.roundtripPricing &&
      'showCalendarRoundtripPrices' in content?.dateAggregator?.roundtripPricing
    ) {
      showCalendarRoundtripPrices = booleanStringCheck(
        content.dateAggregator.roundtripPricing.showCalendarRoundtripPrices,
      );
    } else {
      showCalendarRoundtripPrices =
        this.appConfig.roundTrip.showCalendarRoundtripPrices;
    }
    return { content, showCalendarRoundtripPrices };
  }

  readonly calendarMessage$ = this.select(
    this.apiFailed$,
    this.fare$,
    this.pricingEnabled$,
    this.calendarStates$,
    this.intineraryType$,
    this.content$
      .pipe(map(content => this.getShowCalendarRoundtripPricesValue(content)))
      .pipe(
        // only get emissions from the following when round trip pricing is on
        mergeMap(({ content, showCalendarRoundtripPrices }) =>
          combineLatest({
            tripLength: this.tripLength$.pipe(
              filter(() => showCalendarRoundtripPrices),
              startWith(null),
            ),
            departDate: this.departDate$.pipe(
              filter(() => showCalendarRoundtripPrices),
              startWith(null),
            ),
            focusedInput: this.focusedInput$.pipe(
              filter(() => showCalendarRoundtripPrices),
              startWith(null),
            ),
            roundtripPricing: of(content?.dateAggregator?.roundtripPricing),
            showCalendarRoundtripPrices: of(showCalendarRoundtripPrices),
          }),
        ),
      ),
    (
      apiFailed,
      fare,
      pricingEnabled,
      calendarStates,
      itineraryType,
      {
        tripLength,
        departDate,
        focusedInput,
        roundtripPricing = {},
        showCalendarRoundtripPrices,
      },
    ) => {
      if (apiFailed) {
        return calendarStates.serviceFail;
      } else if (pricingEnabled) {
        // Getting messages from CMS if Any REF: DOT-9920
        const {
          roundTripServiceSuccessCashDepart,
          roundTripServiceSuccessCashReturn,
          roundTripServiceSuccessPointsDepart,
          roundTripServiceSuccessPointsReturn,
        } = roundtripPricing;
        // If there are no messages from CMS, get message from AppConfig REF: DOT-9920
        const roundTripMessages = {
          serviceSuccessCashDepart: roundTripServiceSuccessCashDepart
            ? roundTripServiceSuccessCashDepart
            : this.appConfig.roundTrip.serviceSuccessCashDepart,
          serviceSuccessCashReturn: roundTripServiceSuccessCashReturn
            ? roundTripServiceSuccessCashReturn
            : this.appConfig.roundTrip.serviceSuccessCashReturn,
          serviceSuccessPointsDepart: roundTripServiceSuccessPointsDepart
            ? roundTripServiceSuccessPointsDepart
            : this.appConfig.roundTrip.serviceSuccessPointsDepart,
          serviceSuccessPointsReturn: roundTripServiceSuccessPointsReturn
            ? roundTripServiceSuccessPointsReturn
            : this.appConfig.roundTrip.serviceSuccessPointsReturn,
        };

        if (itineraryType === 'RT' && showCalendarRoundtripPrices) {
          if (fare === 'usd') {
            if (
              focusedInput === 'depart' ||
              (focusedInput === 'return' && !departDate)
            ) {
              return roundTripMessages.serviceSuccessCashDepart.replace(
                '[TRIP-LENGTH]',
                String(tripLength),
              );
            } else if (focusedInput === 'return' && departDate) {
              return roundTripMessages.serviceSuccessCashReturn.replace(
                '[DEPART-DATE]',
                format(departDate, 'MMM d'),
              );
            }
          } else {
            // points
            if (
              focusedInput === 'depart' ||
              (focusedInput === 'return' && !departDate)
            ) {
              return roundTripMessages.serviceSuccessPointsDepart.replace(
                '[TRIP-LENGTH]',
                String(tripLength),
              );
            } else if (focusedInput === 'return' && departDate) {
              return roundTripMessages.serviceSuccessPointsReturn.replace(
                '[DEPART-DATE]',
                format(departDate, 'MMM d'),
              );
            }
          }
        }

        if (fare === 'usd') {
          return calendarStates.serviceSuccessCash;
        } else {
          return calendarStates.serviceSuccessPoints;
        }
      } else {
        return null;
      }
    },
  );

  getCurrentMonthCache$ = (year: number, month: number) =>
    this.select(state => state.monthPricingCache[`${year}-${month}`]);

  getAllMonthsCacheAsArray$ = this.select(this.getAllMonthsCache$, monthCache =>
    Object.keys(monthCache).map(yearMonthString => ({
      year: yearMonthString.slice(0, 4),
      month: yearMonthString.slice(5),
      value: monthCache[yearMonthString],
    })),
  );

  readonly vacationsLinkEnabled$ = this.select(
    this.content$,
    this.intineraryType$,
    (content, intineraryType) =>
      content.vacationsLink?.enableLink?.toLocaleLowerCase() === 'true' &&
      intineraryType === BOOKER_CONFIG.SEARCH_TYPES.ROUND_TRIP,
  );

  readonly vacationsLink$ = this.select(
    this.originCode$,
    this.destinationCode$,
    (originCode, destinationCode) => {
      const dealsLocation = destinationCode
        ? `from/${originCode}/to/${destinationCode}`
        : `from/${originCode}/`;
      return `https://www.jetbluevacations.com/deals/${dealsLocation}?utm_source=jetblue.com&utm_medium=referral&utm_campaign=flight_booker&utm_content=copy_link`;
    },
  );

  readonly vm$ = this.select(
    this.calendarMessage$,
    this.departDate$,
    this.returnDate$,
    this.content$,
    this.scheduleEnabled$,
    this.pricingEnabled$,
    this.bffIntegrationEnabled$,
    this.vacationsLinkEnabled$,
    this.vacationsLink$,
    (
      calendarMessage,
      departDate,
      returnDate,
      content,
      scheduleEnabled,
      pricingEnabled,
      bffIntegrationEnabled,
      vacationsLinkEnabled,
      vacationsLink,
    ) => ({
      calendarMessage,
      departDate,
      returnDate,
      content,
      scheduleEnabled,
      pricingEnabled,
      bffIntegrationEnabled,
      vacationsLinkEnabled,
      vacationsLink,
    }),
  );

  setMonthPricingCache = this.updater<{
    fareResults: BestFareResult;
    year: number;
    month: number;
  }>(
    (state: BookerAirState, { fareResults, year, month }): BookerAirState => ({
      ...state,
      monthPricingCache: {
        ...state.monthPricingCache,
        [`${year}-${month}`]: fareResults,
      },
    }),
  );

  clearMonthPricingCache = this.updater(
    (state: BookerAirState): BookerAirState => ({
      ...state,
      monthPricingCache: {},
    }),
  );

  readonly getBffSingleResponse = this.effect<{
    month: Date;
    datePickerRef: any;
  }>(props$ =>
    props$.pipe(
      withLatestFrom(
        this.originCode$,
        this.destinationCode$,
        this.intineraryType$,
        this.fare$,
        this.travelers$,
        this.calendarStates$,
        this.scheduleEnabled$,
        this.pricingEnabled$,
      ),
      filter(
        ([
          _,
          originCode,
          destinationCode,
          ,
          ,
          { adults, children, infants },
          ,
          ,
          ,
        ]) =>
          Boolean(originCode) &&
          Boolean(destinationCode) &&
          adults + children + infants > 0,
      ),
      tap(() => {
        this.updateApiFailed(false);
      }),
      mergeMap(params => {
        const [
          props,
          originCode,
          destinationCode,
          intineraryType,
          fare,
          pax,
          calendarStates,
          scheduleEnabled,
          pricingEnabled,
        ] = params;

        if (!scheduleEnabled && !pricingEnabled) {
          enableAllDates(
            props.month.getMonth(),
            props.month.getFullYear(),
            props.datePickerRef,
          );
          return of(null);
        }

        return this.bffService
          .getBestFares(
            originCode,
            destinationCode,
            fare === BOOKER_CONFIG.FARE_TYPES.POINTS,
            props.month.getMonth(),
            props.month.getFullYear(),
            intineraryType,
            pax,
            true,
          )
          .pipe(
            tap(response => {
              if (response['apiFailure']) {
                this.updateApiFailed(true);
                const month = props.month.getMonth();
                const year = props.month.getFullYear();

                enableAllDates(month, year, props.datePickerRef);
              }
            }),
            filter(response => !response['apiFailure']),
            tap(response => {
              this.setMonthPricingCache({
                fareResults: response,
                year: props.month.getFullYear(),
                month: props.month.getMonth() + 1,
              });

              const currencySymbol = getCurrencySymbol(
                response.currencyCode,
                'narrow',
              );

              const jbMonthObj = createJBMonthObjForCL(
                props.month,
                response,
                currencySymbol,
                calendarStates.serviceSuccessNoFlights,
                scheduleEnabled,
                pricingEnabled,
                fare,
              );

              props.datePickerRef.setMonthData(jbMonthObj);
            }),
          );
      }),
    ),
  );

  readonly bookerReady$ = this.select(
    this.travelers$,
    this.originCode$,
    this.vm$,
    (travelers, origin, vm) => {
      return { travelers, origin, vm };
    },
  );

  constructor(
    private bffService: BffService,
    private appConfig: AppConfigService,
  ) {
    super(bookerAirComponentInitialState);
  }
}
